import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { CampusResourceCard } from '@/components/campusResource/CampusResourceCard';
import { CampusResourceFormModal } from '@/components/campusResource/CampusResourceFormModal';
import { CustomButton } from '@/components/uiWrappers/button';
import { IoAddOutline } from 'react-icons/io5';
import { College } from '@mightybyte/business';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type UniversityFilter = College | 'all';

const UNIVERSITY_LABELS: Record<College, string> = {
  [College.LMU]: 'LMU',
  [College.Lehigh]: 'Lehigh',
};

export function CampusResourcesScreen() {
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [universityFilter, setUniversityFilter] = useState<UniversityFilter>('all');

  const { data, isLoading } = trpc.admin.campusResource.getCampusResources.useQuery({
    university: universityFilter === 'all' ? undefined : universityFilter,
  });

  const resources = data?.resources ?? [];

  return (
    <div className="flex-1">
      <CampusResourceFormModal
        isOpen={isNewModalOpen}
        onClose={() => setIsNewModalOpen(false)}
      />

      {/* Header */}
      <div className="flex justify-between items-center gap-4 flex-wrap mb-6">
        <div className="flex items-center gap-4">
          {/* University filter */}
          <div className="min-w-[200px]">
            <Select
              value={universityFilter}
              onValueChange={(value) => setUniversityFilter(value as UniversityFilter)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Filter by university" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Universities</SelectItem>
                {Object.entries(UNIVERSITY_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Create button */}
        <CustomButton onClick={() => setIsNewModalOpen(true)}>
          <IoAddOutline className="mr-1" size={20} />
          Add Resource
        </CustomButton>
      </div>

      {/* Resources Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand"></div>
        </div>
      ) : resources.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg mb-4">
            {universityFilter === 'all'
              ? 'No campus resources yet'
              : `No campus resources for ${UNIVERSITY_LABELS[universityFilter]}`}
          </p>
          <CustomButton onClick={() => setIsNewModalOpen(true)}>
            <IoAddOutline className="mr-1" size={20} />
            Add First Resource
          </CustomButton>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {resources.map((resource) => (
            <CampusResourceCard key={resource._id} resource={resource} />
          ))}
        </div>
      )}
    </div>
  );
}